import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { PageLightbox } from './PageLightbox';
import { lookInsidePages } from '../data/lookInsidePages';
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const getStep = () => {
    const track = trackRef.current;
    const first = track?.children[0] as HTMLElement | undefined;
    if (!track || !first) return 0;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.offsetWidth + gap;
  };
  const updateState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const step = getStep();
    const maxScroll = track.scrollWidth - track.clientWidth;
    setAtStart(track.scrollLeft <= 4);
    setAtEnd(track.scrollLeft >= maxScroll - 4);
    if (step > 0) {
      const idx = track.scrollLeft >= maxScroll - 4 ? lookInsidePages.length - 1 : Math.round(track.scrollLeft / step);
      setActive(Math.min(idx, lookInsidePages.length - 1));
    }
  }, []);
  useEffect(() => {
    updateState();
    window.addEventListener('resize', updateState);
    return () => window.removeEventListener('resize', updateState);
  }, [updateState]);
  const scrollByCards = (dir: 1 | -1) => {
    trackRef.current?.scrollBy({
      left: dir * getStep(),
      behavior: 'smooth'
    });
  };
  const scrollToIndex = (i: number) => {
    trackRef.current?.scrollTo({
      left: i * getStep(),
      behavior: 'smooth'
    });
  };
  return <section aria-labelledby="look-inside-heading" className="w-full bg-cream text-ink" data-id="element-132" data-mp="3seudw15zbmrm">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-133" data-mp="2zrzvrawmwzhb">
        <div className="text-center" data-id="element-134" data-mp="2zrgnklwsdpgh">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-deep" data-id="element-135" data-mp="13ov3xu5m4h6r">
            Look inside
          </p>
          <h2 id="look-inside-heading" className="mt-3 font-display font-semibold text-plum text-3xl sm:text-5xl" data-id="element-136" data-mp="18n546j6727z8">
            Open the practice. Run the clock. Coach.
          </h2>
          <p className="mx-auto mt-4 max-w-[60ch] text-base text-ink" data-id="element-137" data-mp="1heynueh0ryt1">
            Real pages from the pack: every practice on two pages, with the
            Rookie and Returner tracks on the same clock.
          </p>
        </div>

        <div className="relative mt-10" data-id="element-138" data-mp="pqx55x3rytcd">
          <ul ref={trackRef} onScroll={updateState} className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-5 px-5 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:gap-6 md:scroll-px-0 md:px-0" data-id="element-139" data-mp="tqe9uobdnmgh">
            {lookInsidePages.map((page, i) => <li key={page.caption} className="w-[82%] shrink-0 snap-start sm:w-[calc((100%-1rem)/2)] md:w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-3rem)/3)]" data-id="element-140" data-mp="2a3cneynrb36p" data-mpi={i}>
                <figure data-id="element-141" data-mp="2fajac2en1qhx">
                  <button type="button" onClick={() => setLightboxIndex(i)} aria-label={`Open full size: ${page.caption}`} className="block w-full overflow-hidden rounded-xl border border-plum-line bg-plum-deep shadow-card transition-transform duration-200 ease-out hover:-translate-y-0.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50" data-id="element-142" data-mp="2aj1a5gkfvty0">
                    <img src={page.src} alt={page.caption} loading="lazy" width={1600} height={2000} className="aspect-[4/5] w-full object-contain" data-id="element-143" data-mp="1rkp07slpeg1d" />
                  </button>
                  <figcaption className="mt-3 text-center text-sm text-ink-soft" data-id="element-144" data-mp="3hurzy2rxhs8e">
                    {page.caption}
                  </figcaption>
                </figure>
              </li>)}
          </ul>

          <button type="button" onClick={() => scrollByCards(-1)} disabled={atStart} aria-label="Previous pages" className="absolute -left-5 top-[calc(50%-1.5rem)] hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-plum text-white shadow-card transition-[background-color,opacity] duration-150 hover:bg-plum-mid focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 disabled:pointer-events-none disabled:opacity-30 md:flex lg:-left-6" data-id="element-145" data-mp="3fzegf21qydf8">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-146" data-mp="31arqk6ehu39r" />
          </button>
          <button type="button" onClick={() => scrollByCards(1)} disabled={atEnd} aria-label="Next pages" className="absolute -right-5 top-[calc(50%-1.5rem)] hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-plum text-white shadow-card transition-[background-color,opacity] duration-150 hover:bg-plum-mid focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 disabled:pointer-events-none disabled:opacity-30 md:flex lg:-right-6" data-id="element-147" data-mp="2nbs2azuw7qe4">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-148" data-mp="2dp7lmup0uk14" />
          </button>
        </div>

        <div className="mt-4 flex justify-center gap-2" role="tablist" aria-label="Choose page" data-id="element-149" data-mp="2rk9t3m9iohg1">
          {lookInsidePages.map((page, i) => <button key={page.caption} type="button" role="tab" aria-selected={active === i} aria-label={`Go to page ${i + 1}: ${page.caption}`} onClick={() => scrollToIndex(i)} className="flex h-6 w-6 items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-full" data-id="element-150" data-mp="31be4yd3wbm6n" data-mpi={i}>
              <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${active === i ? 'w-6 bg-plum' : 'w-2 bg-blush-dark'}`} data-id="element-151" data-mp="23261zltx7jva" />
            </button>)}
        </div>

        <div className="mt-10 flex flex-col items-center" data-id="element-152" data-mp="2gst4zq6prvx6">
          <p className="text-sm text-ink-soft" data-id="element-153" data-mp="2lrcqjpheb5p0">
            6 volumes · 90 practices · one-time $27
          </p>
          <div className="mt-4 w-full max-w-md" data-id="element-154" data-mp="2qpnyfuo4r64l">
            <CtaButton href="#pricing" data-id="element-155" data-mp="2qocp7fbx1663">Get all 90 practices</CtaButton>
          </div>
        </div>
      </div>

      <PageLightbox pages={lookInsidePages} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onChange={setLightboxIndex} data-id="element-156" data-mp="2vnfrwt4fb0aw" />
    </section>;
}